import { formatReading, angleDegrees, dimensionForUnit } from "./units.js";

export const E = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&apos;",
      })[c],
  );
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const numeric = (v) => typeof v === "number" && Number.isFinite(v);
export const short = (s, n = 30) =>
  String(s ?? "").length > n
    ? String(s).slice(0, n - 1) + "…"
    : String(s ?? "");
export const simple = (o) => ["simple", "minimal"].includes(o.variant);
export const svgId = (o, suffix) =>
  `${String(o._svgId ?? "control").replace(/[^\w-]/g, "-")}-${String(suffix).replace(/[^\w-]/g, "-")}`;
export const sample = (data, role) =>
  data.values?.[role] ?? data.service?.values?.[role] ?? null;
export const val = (data, role) => sample(data, role)?.value;
export const angle = (data, role) => angleDegrees(sample(data, role));
export const numberValue = (data, role = "value") =>
  !["missing", "disconnected"].includes(sample(data, role)?.quality) &&
  numeric(val(data, role))
    ? val(data, role)
    : null;
export const text = (
  x,
  y,
  value,
  size = 14,
  fill = "var(--text)",
  anchor = "start",
  extra = "",
) =>
  `<text x="${x}" y="${y}" fill="${fill}" font-size="${size}" text-anchor="${anchor}" ${extra}>${E(value)}</text>`;
export const line = (
  x1,
  y1,
  x2,
  y2,
  color = "var(--grid)",
  width = 1,
  extra = "",
) =>
  `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="${width}" ${extra}/>`;
export const rect = (
  x,
  y,
  w,
  h,
  fill = "var(--surface)",
  rx = 12,
  extra = "",
) =>
  `<rect x="${x}" y="${y}" width="${Math.max(0, w)}" height="${Math.max(0, h)}" rx="${rx}" fill="${fill}" ${extra}/>`;
export const circle = (
  x,
  y,
  r,
  fill = "none",
  stroke = "var(--grid)",
  width = 1,
  extra = "",
) =>
  `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${width}" ${extra}/>`;
export const path = (d, color = "var(--accent)", width = 2, extra = "") =>
  `<path d="${d}" ${extra.includes("fill=") ? "" : 'fill="none"'} stroke="${color}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round" ${extra}/>`;
export const point = (cx, cy, r, a) => ({
  x: cx + r * Math.sin((a * Math.PI) / 180),
  y: cy - r * Math.cos((a * Math.PI) / 180),
});
export const arc = (cx, cy, r, start, end) => {
  const a = point(cx, cy, r, start),
    b = point(cx, cy, r, end);
  return `M ${a.x} ${a.y} A ${r} ${r} 0 ${Math.abs(end - start) > 180 ? 1 : 0} ${end >= start ? 1 : 0} ${b.x} ${b.y}`;
};
// A face's named piece (TOM-618): `data-part="name"`, then any other
// attributes, so a page's CSS can find it. The names are in parts.js.
export const part = (name, extra = "") =>
  `data-part="${name}"${extra ? ` ${extra}` : ""}`;
export const tone = (value) =>
  `var(--${["good", "attention", "danger", "muted", "accent", "text"].includes(value) ? value : "accent"})`;
export function reading(data, role, o) {
  try {
    const r = sample(data, role),
      configured = o.roleUnits?.[role] ?? o.unit;
    const unit =
      configured !== "inherit" &&
      dimensionForUnit(configured) &&
      dimensionForUnit(r?.unit) !== dimensionForUnit(configured) &&
      !o.roleUnits?.[role]
        ? "inherit"
        : configured;
    return formatReading(r, {
      ...o,
      unit,
      decimals: o.roleDecimals?.[role] ?? o.decimals,
    });
  } catch {
    return { value: "Unit mismatch", unit: "", quality: "missing" };
  }
}
export function valueBlock(
  data,
  role,
  o,
  { x = 200, y = 155, size = 56, anchor = "middle" } = {},
) {
  const r = reading(data, role, o);
  const fontSize = Math.min(
    size,
    285 / Math.max(1, String(r.value).length * 0.55),
  );
  return (
    text(
      x,
      y,
      r.value,
      fontSize,
      r.quality === "stale" ? "var(--quiet)" : "var(--text)",
      anchor,
      'font-weight="600" letter-spacing="-1.8" data-motion="value"',
    ) + text(x, y + 25, r.unit, 13, "var(--muted)", anchor)
  );
}
export const referenceLabel = (data, roles = []) =>
  [
    ...new Set(roles.map((r) => sample(data, r)?.reference).filter(Boolean)),
  ].join(" · ");
export function missing(label = "Awaiting source") {
  return (
    circle(200, 138, 44, "none", "var(--grid)", 1, part("dial")) +
    text(200, 151, "—", 42, "var(--muted)", "middle", part("value")) +
    text(200, 210, label, 12, "var(--muted)", "middle", part("caption"))
  );
}
